import React, { useState, useRef, KeyboardEvent } from "react";
import {
  Box,
  TextField,
  Avatar,
  Typography,
  useTheme,
  InputAdornment,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { formatVideoTimestamp } from "@/utils/videoUtils";

interface VideoCommentInputProps {
  currentVideoTime: number;
  onSubmit: (comment: string, timestamp: number) => Promise<void>;
  isSubmitting: boolean;
  users?: Array<{ _id: string; fullName: string }>;
  placeholder?: string;
  isImageMode?: boolean;
}

const VideoCommentInput: React.FC<VideoCommentInputProps> = ({
  currentVideoTime,
  onSubmit,
  isSubmitting,
  users = [],
  placeholder,
  isImageMode = false,
}) => {
  const defaultPlaceholder = "Add a reply";
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [comment, setComment] = useState("");
  const [mentionSearch, setMentionSearch] = useState("");
  const [showMentionList, setShowMentionList] = useState(false);
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);

  // Filter users based on mention search
  const filteredUsers = users.filter((user) =>
    user.fullName.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setComment(value);

    // Check for @ mentions
    const cursorPosition = e.target.selectionStart || 0;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtPos = textBeforeCursor.lastIndexOf("@");

    if (lastAtPos >= 0) {
      const isMention =
        lastAtPos === 0 ||
        textBeforeCursor[lastAtPos - 1] === " " ||
        textBeforeCursor[lastAtPos - 1] === "\n";

      if (isMention) {
        const mentionText = textBeforeCursor.substring(lastAtPos + 1);
        setMentionSearch(mentionText);
        setShowMentionList(true);
        setSelectedUserIndex(0);
        return;
      }
    }

    setShowMentionList(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showMentionList || filteredUsers.length === 0) {
      // Submit on Enter (without Shift)
      if (e.key === "Enter" && !e.shiftKey && comment.trim()) {
        e.preventDefault();
        handleSubmit();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedUserIndex((prev) =>
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedUserIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedUserIndex >= 0 && selectedUserIndex < filteredUsers.length) {
          handleMentionSelect(filteredUsers[selectedUserIndex]);
        }
        break;
      case "Escape":
        setShowMentionList(false);
        break;
    }
  };

  const handleMentionSelect = (user: { _id: string; fullName: string }) => {
    const cursorPosition = inputRef.current?.selectionStart || 0;
    const textBeforeCursor = comment.substring(0, cursorPosition);
    const lastAtPos = textBeforeCursor.lastIndexOf("@");
    const textAfterCursor = comment.substring(cursorPosition);

    const newComment =
      comment.substring(0, lastAtPos) + `@${user.fullName} ` + textAfterCursor;

    setComment(newComment);
    setShowMentionList(false);
    setMentionSearch("");

    // Focus back on input
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleSubmit = async () => {
    if (!comment.trim()) return;

    try {
      await onSubmit(comment.trim(), currentVideoTime);
      setComment("");
    } catch (error) {
      console.error("Error submitting comment:", error);
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Comment Input with inline button */}
      <TextField
        fullWidth
        placeholder={placeholder || defaultPlaceholder}
        value={comment}
        onChange={handleCommentChange}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        disabled={isSubmitting}
        size="small"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "24px",
            backgroundColor: "#ffffff",
            pr: 0.5,
            border: "1px solid",
            borderColor: theme.palette.divider,
            "&:hover": {
              borderColor: theme.palette.text.secondary,
            },
            "&.Mui-focused": {
              borderColor: theme.palette.primary.main,
            },
            "& fieldset": {
              border: "none",
            },
          },
          "& .MuiInputBase-input": {
            py: 1.25,
            px: 2,
            fontSize: "0.875rem",
            color: theme.palette.text.primary,
            "&::placeholder": {
              color: theme.palette.text.secondary,
              opacity: 1,
            },
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <LoadingButton
                size="small"
                variant={comment.trim() ? "contained" : "outlined"}
                disabled={!comment.trim()}
                onClick={handleSubmit}
                loading={isSubmitting}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  px: 1.5,
                  py: 0.5,
                  minWidth: "auto",
                  borderRadius: "16px",
                  whiteSpace: "nowrap",
                  color: comment.trim() ? "#fff" : theme.palette.text.secondary,
                  backgroundColor: comment.trim() ? theme.palette.primary.main : "transparent",
                  borderColor: comment.trim() ? theme.palette.primary.main : theme.palette.divider,
                  "&:hover": {
                    backgroundColor: comment.trim()
                      ? theme.palette.primary.dark
                      : "rgba(0, 0, 0, 0.04)",
                    borderColor: comment.trim() ? theme.palette.primary.dark : theme.palette.text.secondary,
                  },
                  "&.Mui-disabled": {
                    color: theme.palette.text.secondary,
                    backgroundColor: "transparent",
                    borderColor: theme.palette.divider,
                  },
                }}
              >
                {isImageMode ? "Comment" : `Comment at ${formatVideoTimestamp(currentVideoTime)}`}
              </LoadingButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Mention Dropdown */}
      {showMentionList && filteredUsers.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            mt: 0.5,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            maxHeight: 150,
            overflow: "auto",
            width: "200px",
            backgroundColor: "background.paper",
            zIndex: 1000,
            boxShadow: 2,
          }}
        >
          {filteredUsers.map((user, index) => (
            <Box
              key={user._id}
              sx={{
                p: 1,
                cursor: "pointer",
                bgcolor:
                  selectedUserIndex === index ? "action.hover" : "transparent",
                "&:hover": { bgcolor: "action.hover" },
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
              onClick={() => handleMentionSelect(user)}
            >
              <Avatar sx={{ width: 20, height: 20, fontSize: "0.7rem" }}>
                {user.fullName[0]}
              </Avatar>
              <Typography variant="body2">{user.fullName}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default VideoCommentInput;
